import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { getField } from 'src/common/helper';
import { surgeryNameMappings, getShortName } from 'src/utils/nameMapping';
import { getLabelFromPatientSelect } from 'src/components/quotations/QuotationService';
import PrintConsentFormService from 'src/components/quotations/printQuote/PrintConsentFormService';

const fontTextContent = {
  fontSize: '48px',
  fontWeight: '500',
  lineHeight: '1.3',
  alignContent: 'center'
};

type PrintInscriptionProps = {
  detailData: any;
  patientInfo: any;
  fontSizeScaleSurgery: number;
  fontSizeScaleAnesthesia: number;
};

const courseMenuList = [
  '大事にしたい初回鼻コース（肋軟骨以外を使用） - ',
  '大事にしたい初回鼻コース（肋軟骨を使用） - ',
  '鼻修正コース（肋軟骨を使用） - ※培養検査あり',
  '鼻修正コース（肋軟骨以外を使用） - ※培養検査あり'
]

const PrintInscription: React.FC<PrintInscriptionProps> = (props) => {

  const isVip = getField(props.patientInfo, 'vip', false);
  // const age = `（${getField(props.patientInfo, 'age', '　　')}）`;
  const reserveStartTime = getField(props.detailData, 'reserve_start_time', "")
  const reserveEndTime = getField(props.detailData, 'reserve_end_time', "")

  const hasHifuOrDensity = () => {
    return props.detailData &&
      props.detailData['items']
        ?.some((item: any) =>
          item &&
          item['surgery'] &&
          item['surgery'].length !== 0 &&
          (
            getField(item.surgery, 'surgery').includes('HIFU')
            || getField(item.surgery, 'surgery').includes('ハイフ')
            || getField(item.surgery, 'surgery').includes('DENSITY')
            || getField(item.surgery, 'surgery').includes('デンシティ')
          )
        )
  }

  interface TargetDefinition {
    keyword: string;
    separator?: string;
    pattern?: RegExp;
    extractPosition?: number;
    outputFormat?: (prefix: string, parts: string[]) => string;
    specialGrouping?: boolean;
  }

  const targetDefinitions: TargetDefinition[] = [
    {
      keyword: 'アラガンボトックス',
      pattern: /アラガンボトックス[ 　]*-[ 　]*(.+)/,
      outputFormat: (keyword, parts) => `${keyword}(${parts.join('、')})`
    },
    {
      keyword: 'ナボタボトックス',
      pattern: /ナボタボトックス[ 　]*-[ 　]*(.+)/,
      outputFormat: (keyword, parts) => `${keyword}(${parts.join('、')})`
    },
    {
      keyword: '脂肪注入',
      pattern: /脂肪注入料（(.+?)）$/,
      outputFormat: (keyword, parts) => `脂肪注入(${parts.join('、')})`
    },
    {
      keyword: 'CO2',
      pattern: /CO2フラクショナルレーザー(.+)/,
      outputFormat: (keyword, parts) => `CO2フラクショナルレーザー(${parts.join('、')})`
    },
    {
      keyword: 'PRPF',
      pattern: /PRPF[ 　](.+)/,
      outputFormat: (keyword, parts) => `PRPF(${parts.join('、')})`
    },
    {
      keyword: 'デンシティ',
      pattern: /デンシティ[ 　]*-[ 　]*(.+)/,
      outputFormat: (keyword, parts) => `${keyword}(${parts.join('、')})`
    },
    {
      keyword: 'リジェボト',
      pattern: /リジェボト[(（](.+?)[)）]※(.+?)使用[\s　]*(.+)/,
      specialGrouping: true,
    },
    {
      keyword: 'ナノファット脂肪注入',
      pattern: /脂肪注入料（(.+?)）ナノファット/,
      outputFormat: (keyword, parts) => `ナノファット脂肪注入(${parts.join('、')})`
    }
  ];

  const convertList = (list: any) => {
    if (!list || list.length === 0) {
      return [];
    }

    const targetList = list
      .filter(
        (item: any) =>
          item &&
          item['surgery'] &&
          item['surgery'].length !== 0 &&
          getField(item.surgery, 'categoryid') !== 1 &&
          !courseMenuList.includes(getField(item.surgery, 'surgery')) &&
          PrintConsentFormService.canDisplaySurgery(item['surgery']['surgery']) &&
          PrintConsentFormService.canDisplaySurgeryInscription(item['surgery']['surgery'])
      )
      .map((item: any) => {
        const surgery = {
          ...item.surgery,
          surgery: getShortName(surgeryNameMappings, getField(item.surgery, 'surgery'))
        };

        let outItem = { ...item, surgery };
        return outItem;
      });

    let processedItems = [...targetList];
    
    const rigebotoItems = processedItems.filter(item => 
      getField(item.surgery, 'surgery').includes('リジェボト')
    );
    
    if (rigebotoItems.length > 0) {
      const rigebotoGroups: Record<string, { areas: { area: string, amount: string }[] }> = {};
      
      rigebotoItems.forEach(item => {
        const surgeryName = getField(item.surgery, 'surgery');
        const match = surgeryName.match(/リジェボト[(（](.+?)[)）]※(.+?)使用[\s　]*/);
        
        if (match && match.length > 2) {
          const area = match[1];
          const material = match[2];
          // const amount = match[3];
          const amount = "";
          
          if (!rigebotoGroups[material]) {
            rigebotoGroups[material] = { areas: [] };
          }
          
          rigebotoGroups[material].areas.push({ area, amount });
        }
      });
      
      const rigebotoResults = Object.entries(rigebotoGroups).map(([material, group]) => {
        const firstMatchItem = rigebotoItems.find(item => 
          getField(item.surgery, 'surgery').includes(`※${material}使用`)
        );
        
        if (firstMatchItem) {
          const newItem = { ...firstMatchItem };
          const newSurgery = { ...newItem.surgery };
          
          const areasWithAmounts = group.areas.map(({ area, amount }) => `${area}${amount}`);
          
          newSurgery.surgery = `リジェボト(${areasWithAmounts.join('、')})※${material}使用`;
          newItem.surgery = newSurgery;
          return newItem;
        }
        return null;
      }).filter(Boolean) as any[];
      
      processedItems = processedItems.filter(item => 
        !getField(item.surgery, 'surgery').includes('リジェボト')
      ).concat(rigebotoResults);
    }
    
    targetDefinitions.filter(def => def.keyword !== 'リジェボト').forEach(targetDef => {
      const { keyword, pattern, outputFormat } = targetDef;
      
      const matchGroups: Record<string, { keyword: string, uniqueValues: string[], sort: number }> = {};
      const remainingItems: any[] = [];

      processedItems.forEach((item, index) => {
        const surgeryName = getField(item.surgery, 'surgery').trim();
        
        let part = '';
        const match = surgeryName.match(pattern);

        if (match && match.length > 1) {
          part = match[1];
          if (!matchGroups[keyword]) {
            matchGroups[keyword] = {
              keyword,
              uniqueValues: [],
              sort: index
            };
          }
          matchGroups[keyword].uniqueValues.push(part.trim());
        } else {
          remainingItems.push(item);
        }
      });

      Object.entries(matchGroups).forEach(([keyword, group]) => {
        if (group.uniqueValues.length > 0) {
          const firstMatchItem = processedItems.find(item => {
            const match = getField(item.surgery, "surgery").match(pattern);
            return match && match.length > 1;
          });
          
          if (firstMatchItem) {
            const newItem = { ...firstMatchItem };
            const newSurgery = { ...newItem.surgery };
            
            const formattedSurgery = outputFormat 
              ? outputFormat(group.keyword, group.uniqueValues)
              : `${group.keyword}(${group.uniqueValues.join('、')})`;
              
            newSurgery.surgery = formattedSurgery;
            newItem.surgery = newSurgery;
            // remainingItems.push(newItem);
            remainingItems.splice(group.sort, 0, newItem);
          }
        }
      });

      processedItems = [...remainingItems];
    });

    return processedItems;
  };


  return (
    <Box sx={{
      width: '297mm',
      height: '210mm',
      overflow: 'hidden',
      p: '4mm',
      position: 'relative',
    }}>
      {/* {isVip && (<Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // backgroundImage: `url('/images/print/vip_background.png')`,
          // backgroundSize: 'cover',
          opacity: 0.1,
          zIndex: -1,
          // zIndex: 1,
          '@media print': {
            backgroundSize: '100% 100%',
          },
        }}
      >
        <Typography 
          textAlign='center' 
          sx={{ 
            fontSize: '240px',
            color: '#B58C29',
            fontWeight: 'bold',
          }}>VIP</Typography>
      </Box>
      )} */}

      <Box sx={{ p: '4mm' }}>
        {/* <Box sx={{ height: '28mm' }}>
          <img src='/images/print/logo_resonus.svg' alt='logo' />
        </Box> */}
        <Stack spacing={'2.4mm'} sx={{ mt: '2.4mm' }}>
          <Stack spacing={'1.2mm'} alignItems='flex-start'>
            <Box display={'flex'} justifyContent={'row'}>
              <Typography sx={{ ...fontTextContent, fontSize: '68px', mr: '2.2mm' }}>
                {`${getField(props.detailData, 'patientkana')}様`}
              </Typography>
              <Typography sx={{ ...fontTextContent, fontSize: '68px', lineHeight: '1.2' }}>
                {`（${getField(props.patientInfo, 'age', '　　')}）`}
              </Typography>
            </Box>
            <Box display={'flex'} justifyContent={'space-between'} sx={{ width: '100%' }}>
              <Typography sx={{ ...fontTextContent, lineHeight: '1.0'}}>
                {reserveStartTime && reserveEndTime ? `${reserveStartTime}～${reserveEndTime}` : '　　:　　～　　:　　'}
              </Typography>
              {hasHifuOrDensity() && (
                <Typography sx={{ ...fontTextContent, lineHeight: '1.0'}}>
                  {'身長・体重　　cm　　kg'}
                </Typography>
              )}
            </Box>
          </Stack>

          <Box>
          {props.detailData &&
            props.detailData['items']
              ?.filter((item: any) =>
                item &&
                item['surgery'] &&
                item['surgery'].length !== 0 &&
                courseMenuList.includes(getField(item.surgery, 'surgery'))
              )
              .map((item: any, index: number) => (
                <Typography
                  key={index}
                  sx={{ ...fontTextContent, fontSize: '44px' }}
                >
                  {getField(item.surgery, 'surgery') +
                    (getField(item, 'note', '')
                      ? '（' + getField(item, 'note') + '）'
                      : '')}
                </Typography>
              ))}
          </Box>

          <Box>
          {props.detailData && convertList(props.detailData['items'])
              .map((item: any, index: number, filteredItems: any[]) => (
                <Box key={index} display={'inline-flex'}>
                  <Typography
                    key={index}
                    sx={{ ...fontTextContent, fontSize: `${props.fontSizeScaleSurgery}px` }}
                  >
                    {getField(item.surgery, 'surgery') +
                      (getField(item, 'note', '')
                        ? '（' + getField(item, 'note') + '）'
                        : '') +
                      (index < filteredItems.length - 1 ? ' + ' : '')}
                  </Typography>
                </Box>
              ))}
          </Box>

          <Box width="100%" sx={{position: 'absolute', bottom: '4mm'}}>
            <Box display={'flex'} justifyContent={'row'}>
              <Box display={'flex'} justifyContent={'row'} sx={{ mr: '2.0mm' }}>
                <Typography sx={{ ...fontTextContent, fontSize: `${props.fontSizeScaleAnesthesia}px` }}>
                {isVip && (<Box component={'span'}>{'VIP'}</Box>)} {getLabelFromPatientSelect(getField(props.detailData, 'patient_select', 1))} {getField(props.detailData, 'type')}
                </Typography>
              </Box>
              <Box display={'flex'} justifyContent={'row'} gap={'1rem'}>
                {props.detailData
                  && props.detailData['items']
                  && props.detailData['items'].map((item: any, index: number) => (
                    item && (item['surgery'] && item['surgery'].length !== 0 
                      && getField(item.surgery, 'categoryid') === 1 
                      && !courseMenuList.includes(getField(item.surgery, 'surgery'))
                      && getField(item.surgery, 'surgery') !== ' 全身麻酔 -  術後管理料'
                      && PrintConsentFormService.canDisplaySurgery(getField(item.surgery, 'surgery'))
                    ) ? (
                    <Typography key={index} sx={{ ...fontTextContent, fontSize: `${props.fontSizeScaleAnesthesia}px` }}>
                      {getShortName(surgeryNameMappings, getField(item.surgery, 'surgery')) +
                        (getField(item, 'note', '')
                          ? '（' + getField(item, 'note') + '）'
                          : '')
                      }
                    </Typography>
                    ) : null
                  ))}         
                <Typography sx={{ ...fontTextContent, fontSize: `${props.fontSizeScaleAnesthesia}px` }}>
                  {(props.detailData?.items || []).some(
                    (item: any) => getField(item.surgery, 'surgery') === ' 全身麻酔 -  術後管理料'
                  ) ? '*オーバーステイ' : ''}
                </Typography>
              </Box>
            </Box>

            <Box display="flex" justifyContent="space-between" width="100%">
              <Typography sx={{ ...fontTextContent, flex: 1 }}>
                {getField(props.detailData, 'reserve_date', '')}
              </Typography>
              <Typography sx={{ ...fontTextContent, flex: 1 }}>
                {`ID:${getField(props.detailData, 'patientno')}`}
              </Typography>
              <Typography sx={{ ...fontTextContent, flex: 1 }}>
                {getField(props.detailData, 'doctor')}
              </Typography>
            </Box>
          </Box>

        </Stack>
      </Box >
    </Box >
  );
}

export default PrintInscription;
